import ObjectHash from 'object-hash'
import { Buffer } from 'buffer'

export const state = () => {
	return {
		rewards: [],
		next: null,
		ttl_rewards: null,
		cache_duration_rewards: 1800000, // 30 minutes
		cache_key_rewards: '',
		reward: {},
		ttl_reward: null,
		cache_duration_reward: 1800000,
		cache_key_reward: '',
	}
}

function isInCacheRewards(state, companyId, params) {
	if (state.cache_key_rewards !== buildCacheKeyRewards(companyId, params)) {
		return false
	}
	if (state.ttl_rewards === null || state.ttl_rewards < Date.now()) {
		return false
	}
	return true
}

function buildCacheKeyRewards(companyId, params) {
	return `company:${companyId}:rewards:${ObjectHash(params)}`
}

function isInCacheReward(state, companyId, rewardId) {
	if (state.cache_key_reward !== buildCacheKeyReward(companyId, rewardId)) {
		return false
	}
	if (state.ttl_reward === null || state.ttl_reward < Date.now()) {
		return false
	}
	return true
}

function buildCacheKeyReward(companyId, rewardId) {
	return `company:${companyId}:c:${rewardId}`
}

export const actions = {
	async count(_, { companyId, params = {} }) {
		params.limit = 1
		return await useNuxtApp()
			.$api(`api/company/${companyId}/rewards/`, { params })
			.then((res) => {
				return res.count
			})
	},
	async list({ state, commit }, { companyId, params = {} }) {
		if (isInCacheRewards(state, companyId, params)) {
			return state.rewards
		}
		commit('clearNext')

		let listUrl = `api/company/${companyId}/rewards/`
		const { status, promo_type, ...restParams } = params
		if (status) status.forEach((tc) => (listUrl += `&status[]=${tc}`))
		if (promo_type) promo_type.split(',').forEach((pt) => (listUrl += `&promo_type=${pt}`))
		listUrl = listUrl.replace('&', '?')

		return await useNuxtApp()
			.$api(listUrl, { params: restParams })
			.then((res) => {
				commit('setRewards', { data: res, companyId, params })
				if (params.limit) {
					return res.results
				} else {
					return res
				}
			})
			.catch((err) => {
				commit('clearCacheRewards')
				throw err
			})
	},

	async get_page({ commit, state }, { companyId }) {
		const next = state.next
		commit('clearNext')
		return await useNuxtApp()
			.$api(next)
			.then((res) => {
				commit('setRewardsPaginate', { data: res, companyId, params: { next } })
				return res
			})
	},

	async upload(_, { companyId, extension, file_type: fileType, data }) {
		return await useNuxtApp()
			.$api(`api/company/${companyId}/rewards/signed_url`, {
				params: { extension },
			})
			.then(async (res) => {
				await $fetch(res.signed_url, {
					method: 'PUT',
					body: Buffer.from(data.replace(/^data:\w+\/\w+;base64,/, ''), 'base64'),
					headers: { 'Content-Type': fileType },
				})
				return res.remote_url
			})
	},

	async retrieve({ state, commit }, { companyId, rewardId }) {
		if (isInCacheReward(state, companyId, rewardId)) {
			return state.reward
		}
		return await useNuxtApp()
			.$api(`api/company/${companyId}/rewards/${rewardId}/`)
			.then((res) => {
				commit('setReward', { data: res, companyId, rewardId })
				return res
			})
			.catch((err) => {
				commit('clearCacheReward')
				throw err
			})
	},

	async create({ commit }, { companyId, reward }) {
		return await useNuxtApp()
			.$api(`api/company/${companyId}/rewards/`, {
				method: 'POST',
				body: reward,
			})
			.then((res) => {
				commit('clearCacheReward')
				commit('clearCacheRewards')
				commit('viptierLevels/invalidateCacheLevel', null, { root: true })
				return res
			})
			.catch((err) => {
				commit('clearCacheReward')
				commit('clearCacheRewards')
				commit('viptierLevels/invalidateCacheLevel', null, { root: true })
				throw err
			})
	},

	async duplicate({ dispatch, commit }, { companyId, rewardId }) {
		const existingReward = await dispatch('retrieve', { companyId, rewardId })

		let duplicatedData = { ...existingReward }
		delete duplicatedData.id
		duplicatedData.name = `Clone : "${existingReward.name}"`.substring(0, 50)
		duplicatedData.draft = true

		// Remove properties with null values
		duplicatedData = Object.fromEntries(
			// eslint-disable-next-line no-unused-vars
			Object.entries(duplicatedData).filter(([_, value]) => value !== null),
		)

		// need to format promocode to string
		if (existingReward.promocode_unique_name) {
			if (Array.isArray(existingReward.promocode_unique_name)) {
				duplicatedData.promocode = existingReward.promocode_unique_name.join('\n')
			} else {
				duplicatedData.promocode = existingReward.promocode_unique_name
			}
		}

		const newReward = await await useNuxtApp()
			.$api(`api/company/${companyId}/rewards/`, {
				method: 'POST',
				body: duplicatedData,
			})
			.then((res) => {
				commit('invalidateCacheRewards')
				commit('invalidateCacheReward')
				commit('viptierLevels/invalidateCacheLevel', null, { root: true })
				return res
			})

		return newReward
	},

	async delete({ commit, dispatch }, { companyId, rewardId }) {
		return await useNuxtApp()
			.$api(`api/company/${companyId}/rewards/${rewardId}/`, { method: 'DELETE' })
			.then((res) => {
				commit('invalidateCacheRewards')
				commit('invalidateCacheReward')
				commit('viptierLevels/invalidateCacheLevel', null, { root: true })
				dispatch('list', { companyId })
				return res
			})
			.catch((err) => {
				commit('invalidateCacheRewards')
				commit('invalidateCacheReward')
				commit('viptierLevels/invalidateCacheLevel', null, { root: true })
				dispatch('list', { companyId })
				throw err
			})
	},

	async partial_update({ dispatch, commit }, { companyId, reward, data }) {
		return await useNuxtApp()
			.$api(`api/company/${companyId}/rewards/${reward.id}/`, {
				method: 'PATCH',
				body: data,
			})
			.then((res) => {
				commit('invalidateCacheRewards')
				commit('viptierLevels/invalidateCacheLevel', null, { root: true })
				commit('invalidateCacheReward')
				commit('patchReward', { data: res, companyId, rewardId: res.id })
			})
			.catch((err) => {
				dispatch('retrieve', {
					companyId,
					rewardId: reward.id,
				})
				commit('viptierLevels/invalidateCacheLevel', null, { root: true })
				commit('invalidateCacheRewards')
				commit('invalidateCacheReward')
				throw err
			})
	},

	async exportPromocodes(_, { companyId, reward }) {
		return await useNuxtApp()
			.$api(`api/company/${companyId}/rewards/${reward.id}/promocodes/?export=true`)
			.then((res) => {
				// create downloadable CSV file from data
				const url = window.URL.createObjectURL(new Blob([res]))
				const link = document.createElement('a')
				link.href = url
				link.setAttribute('download', `${reward.name}.csv`)
				document.body.appendChild(link)
				link.click()
				link.remove()
			})
	},
}

export const mutations = {
	setRewards(state, { data, companyId, params }) {
		if (params.limit) {
			state.rewards = data.results
			state.next = data.next
		} else {
			state.rewards = data
		}

		state.ttl_rewards = Date.now() + state.cache_duration_rewards
		state.cache_key_rewards = buildCacheKeyRewards(companyId, params)
	},

	patchReward(state, { data, rewardId }) {
		const index = state.rewards.findIndex((reward) => reward.id === rewardId)
		if (index !== -1) {
			state.rewards[index] = data
		}
		state.ttl_rewards = null
	},

	setRewardsPaginate(state, { data, companyId, params }) {
		state.rewards = state.rewards.concat(data.results)
		state.next = data.next
		state.ttl_rewards = Date.now() + state.cache_duration_rewards
		state.cache_key_rewards = buildCacheKeyRewards(companyId, params)
	},

	setReward(state, { data, companyId, rewardId }) {
		state.reward = data
		state.ttl_reward = Date.now() + state.cache_duration_reward
		state.cache_key_reward = buildCacheKeyReward(companyId, rewardId)
	},

	clearNext(state) {
		state.next = null
	},

	clearCacheRewards(state) {
		state.rewards = []
		state.next = null
		state.ttl_rewards = null
		state.cache_key_rewards = ''
	},

	clearCacheReward(state) {
		state.reward = {}
		state.ttl_reward = null
		state.cache_key_reward = ''
	},

	invalidateCacheRewards(state) {
		state.ttl_rewards = null
	},

	invalidateCacheReward(state) {
		state.ttl_reward = null
	},
}
